import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, BookMarked, ChevronRight, ExternalLink, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  LEXIQUE,
  LEXIQUE_CATEGORIES,
  searchLexique,
  getCategory,
} from '../data/lexique';

/**
 * LexiqueDrawer — panneau latéral du lexique accessible PENDANT un quiz/examen.
 *
 * • Bouton flottant (FAB) en bas à droite — ne masque pas le contenu du quiz.
 * • Sheet shadcn côté droit, recherche live, cards compactes.
 * • Raccourci clavier : touche `L` pour ouvrir/fermer (sauf si focus sur input).
 * • Cliquer sur une card affiche le détail dans un mode "lecture" sans quitter le drawer.
 * • Bouton "Ouvrir la page complète" pour aller sur /lexique.
 *
 * Utilisation :
 *   import { LexiqueDrawer } from '@/components/LexiqueDrawer';
 *   <LexiqueDrawer />          // FAB par défaut
 *   <LexiqueDrawer hideFab />  // si vous voulez un trigger custom
 */

// Carte compacte (drawer)
const DrawerLexiqueCard = ({ entry, query, onSelect }) => {
  const Icon = entry.icon;
  const cat = getCategory(entry.category);
  return (
    <button
      type="button"
      onClick={() => onSelect(entry)}
      className="w-full text-left mc-card p-3 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-start gap-2.5 group"
      data-testid={`lexique-drawer-card-${entry.slug}`}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: entry.soft, color: entry.color }}
      >
        <Icon className="w-4 h-4" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h4 className="text-sm font-black leading-tight truncate">{entry.term}</h4>
          <span
            className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full whitespace-nowrap"
            style={{ background: cat?.soft || '#f3f4f6', color: cat?.color || '#374151' }}
          >
            {cat?.short || 'Lex.'}
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
          {entry.definition}
        </p>
      </div>
      <ChevronRight className="w-3.5 h-3.5 mt-2 flex-shrink-0 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
};

// Vue détail (lorsqu'une carte est sélectionnée dans le drawer)
const DrawerDetail = ({ entry, onBack }) => {
  const Icon = entry.icon;
  const cat = getCategory(entry.category);
  return (
    <div className="space-y-4" data-testid={`lexique-drawer-detail-${entry.slug}`}>
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
        data-testid="lexique-drawer-back"
      >
        <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Retour à la liste
      </button>

      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: entry.soft, color: entry.color }}
        >
          <Icon className="w-6 h-6" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full inline-block mb-1"
            style={{ background: cat?.soft || '#f3f4f6', color: cat?.color || '#374151' }}
          >
            {cat?.label || 'Lexique'}
          </span>
          <h3 className="text-lg font-black leading-tight">{entry.term}</h3>
        </div>
      </div>

      <p className="text-sm text-foreground/85 leading-relaxed">{entry.definition}</p>

      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
          Règles & repères
        </div>
        <ul className="space-y-2">
          {entry.rules.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2"
                style={{ background: entry.color }}
                aria-hidden="true"
              />
              <span className="leading-relaxed text-foreground/85">{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {entry.distinction && (
        <div
          className="rounded-lg p-3 text-sm leading-relaxed flex items-start gap-2"
          style={{ background: `${entry.color}10`, borderLeft: `3px solid ${entry.color}` }}
        >
          <span
            className="font-black uppercase tracking-widest text-[10px] flex-shrink-0 mt-0.5"
            style={{ color: entry.color }}
          >
            Distinction
          </span>
          <span className="text-foreground/85">{entry.distinction}</span>
        </div>
      )}

      <Link
        to={`/lexique#${entry.slug}`}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[hsl(var(--primary))] hover:underline"
        data-testid="lexique-drawer-deep-link"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Voir cette fiche dans le lexique complet
      </Link>
    </div>
  );
};

export const LexiqueDrawer = ({ hideFab = false, trigger = null, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [selected, setSelected] = useState(null);

  // Filtrer
  const results = useMemo(() => {
    let list = searchLexique(query);
    if (activeCat) list = list.filter((e) => e.category === activeCat);
    return list;
  }, [query, activeCat]);

  // Reset sélection quand on ferme
  useEffect(() => {
    if (!open) {
      setSelected(null);
    }
  }, [open]);

  // Raccourci clavier "L" pour toggle (sauf si focus sur input)
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const isEditable = e.target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (isEditable) return;
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open && selected) {
        setSelected(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, selected]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {/* TRIGGER : FAB par défaut, ou trigger custom */}
      {!hideFab && (
        <SheetTrigger asChild data-testid="lexique-fab-trigger">
          {trigger || (
            <button
              type="button"
              aria-label="Ouvrir le lexique du motard (raccourci : L)"
              title="Lexique du motard (L)"
              className={cn(
                // Empilé AU-DESSUS du CoachChatWidget (à bottom-[92px] / sm:bottom-24).
                // z-[65] pour passer au-dessus du Coach (z-60) et du BottomNav (z-40).
                'fixed z-[65] right-3 sm:right-6 bottom-[150px] sm:bottom-40',
                'inline-flex items-center gap-1.5 sm:gap-2 h-11 sm:h-12 px-3 sm:px-4 rounded-full',
                'shadow-[0_8px_24px_-6px_rgba(207,32,49,0.45)] hover:shadow-[0_12px_28px_-6px_rgba(207,32,49,0.6)]',
                'bg-[hsl(var(--primary))] text-white font-bold text-sm',
                'hover:scale-105 active:scale-95 transition-all',
                'ring-4 ring-[hsl(var(--primary)/0.15)]',
              )}
              data-testid="lexique-fab"
            >
              <BookMarked className="w-5 h-5" />
              <span className="hidden sm:inline">Lexique</span>
              <kbd className="hidden md:inline-flex items-center justify-center w-5 h-5 text-[10px] font-black rounded bg-white/20 ml-1">L</kbd>
            </button>
          )}
        </SheetTrigger>
      )}

      <SheetContent
        side="right"
        className="w-[100vw] sm:w-[440px] sm:max-w-[440px] p-0 flex flex-col gap-0"
        data-testid="lexique-drawer-content"
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-gradient-to-br from-[hsl(var(--primary)/0.06)] to-card">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center flex-shrink-0">
              <BookMarked className="w-4 h-4" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">
                Lexique du motard
              </div>
              <div className="text-sm font-black flex items-center gap-1.5">
                {LEXIQUE.length} termes essentiels
                <Sparkles className="w-3 h-3 text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>

        {/* MODE LISTE ou MODE DÉTAIL */}
        {selected ? (
          <div className="flex-1 overflow-y-auto p-4">
            <DrawerDetail entry={selected} onBack={() => setSelected(null)} />
          </div>
        ) : (
          <>
            {/* SEARCH */}
            <div className="flex-shrink-0 px-4 pt-3 pb-2 border-b border-border space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Chercher : CSG, point mort, code 78…"
                  className="pl-9 pr-9 h-10 text-sm rounded-full"
                  aria-label="Rechercher dans le lexique"
                  data-testid="lexique-drawer-search"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                    aria-label="Effacer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Chips catégories — version compacte, scroll horizontal sur mobile */}
              <div
                className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 pb-0.5 scrollbar-thin"
                style={{ scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}
                data-testid="lexique-drawer-filters"
              >
                <button
                  type="button"
                  onClick={() => setActiveCat(null)}
                  className={cn(
                    'flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap',
                    activeCat === null
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-border text-foreground/70 hover:bg-muted/40',
                  )}
                  style={{ scrollSnapAlign: 'start' }}
                  data-testid="lexique-drawer-filter-all"
                >
                  Tout
                </button>
                {LEXIQUE_CATEGORIES.map((c) => {
                  const active = activeCat === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setActiveCat(active ? null : c.id)}
                      className={cn(
                        'flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all whitespace-nowrap',
                        active ? 'shadow-sm' : 'border-border text-foreground/70 hover:bg-muted/40',
                      )}
                      style={{
                        scrollSnapAlign: 'start',
                        ...(active ? {
                          background: c.soft,
                          color: c.color,
                          borderColor: c.color,
                        } : {}),
                      }}
                      data-testid={`lexique-drawer-filter-${c.id}`}
                    >
                      {c.short}
                    </button>
                  );
                })}
              </div>

              <div className="text-[10px] font-semibold text-muted-foreground" data-testid="lexique-drawer-count">
                {results.length} / {LEXIQUE.length} terme{LEXIQUE.length > 1 ? 's' : ''}
              </div>
            </div>

            {/* RÉSULTATS */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="lexique-drawer-list">
              {results.length > 0 ? (
                results.map((entry) => (
                  <DrawerLexiqueCard
                    key={entry.slug}
                    entry={entry}
                    query={query}
                    onSelect={setSelected}
                  />
                ))
              ) : (
                <div className="text-center py-10 px-4">
                  <Search className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-bold">Aucun résultat</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Essayez un autre mot-clé ou réinitialisez les filtres.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 rounded-full"
                    onClick={() => { setQuery(''); setActiveCat(null); }}
                    data-testid="lexique-drawer-reset"
                  >
                    Réinitialiser
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* FOOTER : lien vers la page complète */}
        <div className="flex-shrink-0 border-t border-border px-4 py-3 bg-muted/20">
          <Link
            to="/lexique"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center w-full gap-1.5 h-10 rounded-full text-xs font-bold border border-border bg-card hover:bg-muted/40 transition-colors"
            data-testid="lexique-drawer-open-page"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Ouvrir la page complète /lexique
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LexiqueDrawer;
